import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { ApplicantForm, validateApplicant } from "@/components/ApplicantForm";
import { DocumentsUpload } from "@/components/DocumentsUpload";
import { Button } from "@/components/ui/button";
import { PayMethods } from "@/components/PayMethods";
import { Loader2, ShieldCheck, ArrowLeft, GraduationCap } from "lucide-react";

const money = (n) => `R${Number(n || 0).toFixed(2)}`;

export default function ApplyCollege() {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [f, setF] = useState({});
  const [docs, setDocs] = useState({});
  const [popiaConsent, setPopiaConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    const raw = sessionStorage.getItem("yva_college_cart");
    const c = raw ? JSON.parse(raw) : null;
    if (!c || !c.course_ids?.length) { toast.error("No programmes selected."); navigate("/dashboard/colleges"); return; }
    setCart(c);
  }, [navigate]);

  const submit = async () => {
    const missing = validateApplicant(f);
    if (missing.length) { toast.error(`Please complete all required fields. Missing: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? ` and ${missing.length - 3} more` : ""}.`); return; }
    if (!popiaConsent) { toast.error("Please tick the POPIA consent box to continue."); return; }
    if (!docs.id_copy || (!docs.matric && !docs.results)) { toast.error("Please upload your certified ID copy and your matric certificate or results."); return; }
    setSubmitting(true);
    try {
      const { data: batch } = await api.post("/applications/batch", {
        selections: [{ university: cart.college.code, course_ids: cart.course_ids }],
        applicant: f,
      });
      const { data: pay } = await api.post("/payments/initialize", { type: "application_batch", batch_id: batch.batch_id, callback_url: `${window.location.origin}/payment/callback` });
      sessionStorage.removeItem("yva_college_cart");
      window.location.href = pay.authorization_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); setSubmitting(false); }
  };

  if (!cart) return <div className="min-h-screen bg-[#F8F9FA]"><Header /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div></div>;

  const fee = cart.college.application_fee || 85.99;

  return (
    <div className="min-h-screen bg-[#F8F9FA]"><Header />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard/colleges")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2447] mb-4"><ArrowLeft className="w-4 h-4" /> Back to programmes</button>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">College Application</p>
        <h1 className="font-display text-3xl font-bold text-[#0B2447] mt-1">Applying to {cart.college.name}</h1>
        <p className="text-slate-600 mt-2">Fill in your details once. Our team will process your college application.</p>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="college-summary">
          <div className="flex items-center gap-3"><img src={cart.college.logo_url} alt="" className="w-10 h-10 rounded-lg object-contain bg-white border border-slate-100" /><h3 className="font-display font-bold text-[#0B2447]">{cart.college.name}</h3></div>
          <div className="mt-3 space-y-1">
            {cart.courses.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-sm text-slate-700"><GraduationCap className="w-4 h-4 text-[#F5C32C] shrink-0" /> {c.name}<span className="text-slate-400 text-xs">· {c.level}</span></div>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
          <h2 className="font-display text-xl font-bold text-[#0B2447] mb-4">Your details</h2>
          <ApplicantForm f={f} set={set} />
        </motion.div>

        <div className="mt-6 bg-[#0B2447]/[0.03] border border-[#0B2447]/15 rounded-xl p-4" data-testid="college-popia-box">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={popiaConsent} onChange={(e) => setPopiaConsent(e.target.checked)} data-testid="college-popia-checkbox" className="mt-1 w-4 h-4 accent-[#0B2447]" />
            <span className="text-sm text-slate-700">I consent, under the <strong>Protection of Personal Information Act (POPIA)</strong>, to Your Varsity Application securely storing and using my documents and details <strong>solely to process my college application</strong>.</span>
          </label>
        </div>

        <div className="mt-6"><DocumentsUpload onChange={setDocs} disabled={!popiaConsent} /></div>

        <div className="sm:sticky bottom-4 mt-8">
          <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-5 flex flex-wrap items-center justify-between gap-4">
            <PayMethods className="basis-full" />
            <div><div className="font-display text-2xl font-bold text-[#0B2447]" data-testid="college-total">{money(fee)}</div><p className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> One secure Paystack payment</p></div>
            <Button onClick={submit} disabled={submitting} data-testid="college-submit-btn" className="bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 px-8">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay ${money(fee)} & Submit`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
